import { memo, useContext, type ReactNode } from "react";
import { CellContext } from "@tanstack/react-table";
import classNames from "classnames";
import { getCashItemLabel } from "@common/helpers/reconciliationCashItems";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Menu } from "@design-system/Menu/Menu.lib";
import { StatusPill } from "@design-system/StatusPill/StatusPill";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { CheckIcon } from "@icons";
import {
    CheckMergeSelection,
    ReconciliationContext,
} from "./Reconciliation.context";
import { ReconciliationClearCashItemsContext } from "./ReconciliationClearCashItems.context";
import styles from "./Reconciliation.module.scss";

function ClearActions({
    isAlwaysVisible,
    children,
}: {
    isAlwaysVisible: boolean;
    children: ReactNode;
}) {
    return (
        <HStack
            className={classNames(styles.clearMatchActions, {
                [viewTableStyles.showOnHover]: !isAlwaysVisible,
            })}
            gap={8}
            align="center"
            justify="end"
        >
            {children}
        </HStack>
    );
}

export const ReconciliationClearActionCell = memo(
    function ReconciliationClearActionCell({
        row,
    }: CellContext<ViewTableFeatures, TransactionListItem, unknown>) {
        const { checkMerges, setCheckToMatch, unregisterCheckMerge } =
            useContext(ReconciliationContext);
        const { cashItemsByTransactionId, firstOutstandingTransactionId } =
            useContext(ReconciliationClearCashItemsContext);
        const transaction = row.original;
        const merge = checkMerges.find(
            ({ checkTransactionId }) => checkTransactionId === transaction.id,
        );

        if (merge) {
            return (
                <ViewTableCell captureClicks>
                    <MatchedState
                        merge={merge}
                        onUndo={() => unregisterCheckMerge(transaction.id)}
                    />
                </ViewTableCell>
            );
        }

        const cashItem = cashItemsByTransactionId.get(transaction.id);

        return (
            <ViewTableCell captureClicks>
                <ClearActions
                    isAlwaysVisible={
                        firstOutstandingTransactionId === transaction.id
                    }
                >
                    <Button
                        variant="primary"
                        size="sm"
                        disabled={!cashItem}
                        onClick={() => setCheckToMatch(cashItem)}
                    >
                        Match {getCashItemLabel(cashItem?.type)}
                    </Button>
                </ClearActions>
            </ViewTableCell>
        );
    },
);

function MatchedState({
    merge,
    onUndo,
}: {
    merge: CheckMergeSelection;
    onUndo: () => void;
}) {
    return (
        <HStack
            gap={8}
            align="center"
            justify="end"
            className={styles.clearMatchActions}
        >
            <Tooltip content={`Cleared by ${merge.bankDescription}`}>
                <span>
                    <StatusPill icon={<CheckIcon />} label="Matched" />
                </span>
            </Tooltip>
            <span className={viewTableStyles.showOnHover}>
                <Menu.More aria-label="Match actions">
                    <Menu.Item onClick={onUndo}>Unmatch</Menu.Item>
                </Menu.More>
            </span>
        </HStack>
    );
}
